import Vue from "vue";
import moment from "moment";
import {
  SERVICE,
  ServiceHelper,
  POPULATION_TYPE,
  InformationType,
  InfoTypeHelper,
  PopulationTypeHelper,
} from "./models";
import { ICONS } from "./icons.map";
import { SharedState } from "@/common/state/index";
import { UserRole } from "@/views/admins/data/models";
import { sortBy } from "@/fp";
import { AxisType, EventTypeMap } from "@/gql/types";
import { FindingObjectType } from "@/common/api/findings/findings.model";
import {
  ApplicationType,
  DataSourceType,
} from "@/views/customize/data-sources/data/model";
const { l0InfoTypes, allInfoTypes, infoTypeByShortName, infoTypeByName } =
  SharedState;

const INFO_TYPES_BY_CODE_INDEXES = [
  "",
  "Other",
  "Governance",
  "Business",
  "Financial",
  "HR",
  "Legal",
  "Personal",
  "Microsoft Labels",
];

const compact_number_formatter = Intl.NumberFormat("en", {
  // @ts-ignore
  notation: "compact",
});

export const Filters = {
  percentage: (val: number, total: number) => {
    if (total === 0) {
      return 0;
    }
    return (val * 100) / total;
  },
  date: (str: string, format = "DD/MM/YYYY"): string => {
    if (str) {
      return moment(str /* .substr(0, 10) */).format(format);
    } else {
      return "---";
    }
  },
  ellipsis: (str: string, num: number): string => {
    return (str || "").length > num ? str.slice(0, num) + "..." : str;
  },
  numsWithComma: (num = 0): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  },
  serviceName: (source: SERVICE): string => {
    return ServiceHelper.asString(source);
  },
  humanReadableServiceName(name: string) {
    const val = ServiceHelper.asString(name);
    if (val === "ms_teams") {
      return "Teams";
    }
    return val;
  },
  replace: (val: string, obj: Record<string, string>): string => {
    return obj[val] ? obj[val] : val;
  },
  trimUrl: (url = ""): string => {
    return `..${url.replace(/http(.+)\.com/, "")}`;
  },
  avatar: (name = ""): string => {
    const arr = name.split(" ").filter((val) => val);
    switch (true) {
      case arr.length > 2:
        if (arr[1] === "&") {
          return arr[0][0] + arr[2][0];
        } else {
          return arr[0][0] + arr[1][0];
        }
      case arr.length === 2:
        return arr[0][0] + arr[1][0];
      default:
        return name[0];
    }
  },
  appIcon: (app: ApplicationType): string => {
    return ICONS.APPLICATIONS.get(app);
  },
  dataSourceIcon: (source: DataSourceType): string => {
    return ICONS.DATA_SOURCES.get(source);
  },
  serviceIcon: (service: SERVICE | string): string => {
    if (typeof service === "string") {
      return ICONS.SERVICE.byString(service);
    } else {
      return ICONS.SERVICE.byNumber(service);
    }
  },
  normalize_service_name: (service: string) => {
    const services_map = {
      exchange_365: "Exchange365",
      onedrive: "OneDrive",
      ms_teams: "Teams",
      sharepoint: "SharePoint",
    };
    // @ts-ignore
    const val = services_map[service] || service;
    return val;
  },
  source_icon: (name: string) => {
    return ICONS.SERVICE.byString(name);
  },
  firstContentLabelName: (
    labels: Array<{
      type: string;
      labelName: string;
      labelType: string;
      name: string;
      concrete: number;
    }>,
  ) => {
    const items = sortBy<InformationType, "concrete">(
      //@ts-ignore
      labels.filter(
        (l) =>
          //@ts-ignore
          l.type === "Content_label" || l.labelType === "Content_label",
      ),
      "concrete",
      "desc",
    );
    //@ts-ignore
    return items.length > 0 ? items[0].name || items[0].labelName : "??";
  },
  serviceColor: (service: SERVICE): string => {
    return ServiceHelper.asColor(service);
  },
  decodeUri(str: string): string {
    return decodeURIComponent(str);
  },
  l0Icon: (uid: string): string => {
    const t = allInfoTypes.value[uid];
    return ICONS.L0_ICONS.get(t ? t.code : 0);
  },
  l0IconByShortName: (name: string): string => {
    const t = infoTypeByShortName.value[name];
    return ICONS.L0_ICONS.get(t ? t.code : 0);
  },
  //
  l0IconByName: (name: string): string => {
    const t = infoTypeByName.value[name];
    return ICONS.L0_ICONS.get(t ? t.code : 0);
  },
  l0IconById: (id: string): string => {
    const t = l0InfoTypes.value.find((i: any) => i.id === id);
    return ICONS.L0_ICONS.get(t ? t.code : 1);
  },
  l0IconById2: (id: string): string => {
    const t = allInfoTypes.value[id];
    return ICONS.L0_ICONS.get(t ? t.code : 1);
  },
  l0IconByCode: (code: number): string => {
    return ICONS.L0_ICONS.get(+code);
  },
  l0IconByL1Id: (id: string): string => {
    const infoType: InformationType = allInfoTypes.value[id];
    if (!infoType) {
      return ICONS.L0_ICONS.get(1);
    }

    if (infoType.parentId === "L0") {
      return ICONS.L0_ICONS.get(infoType.code);
    } else {
      return ICONS.L0_ICONS.get(
        allInfoTypes.value[infoType.parentId as string].code || 1,
      );
    }
  },
  label_category: (category: string, is_pii = false) => {
    switch (category) {
      case "PII Regulations":
        return "PII";
      case "PFI Regulations":
        return "PFI";
      case "PCI Regulations":
        return "PCI";
      case "Content":
        if (is_pii) {
          return "SPI";
        } else {
          return "Content";
        }
      default:
        return category;
    }
  },
  sum_by: <T, K extends keyof T>(items: T[], prop: K): number => {
    return items.reduce((acc, item) => {
      acc += item[prop] as unknown as number;
      return acc;
    }, 0);
  },
  iconByAxisType: (type: AxisType) => {
    switch (type) {
      case AxisType.Employee:
        return "User";
      case AxisType.EmployeeSender:
      case AxisType.EmployeeSenderType:
        return "ExposedBy";
      case AxisType.EmployeeReceiver:
      case AxisType.EmployeeReceiverType:
        return "ExposedTo";
      case AxisType.OrganizationReceiver:
      case AxisType.OrganizationReceiverType:
        return "Organization";
      case AxisType.PersonalAddressType:
        return "PrivateEmail";
      case AxisType.ExternalReceiverType:
        return "External";
    }
  },
  nums_with_commas(num: number) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  },
  num_suffix: (
    num: number,
    nums_after_dot = -1,
    always_have = false,
  ): string => {
    const fixed = (n: number) => {
      if (n > 0 && nums_after_dot > -1) {
        const minVisibleValue = 1 / Math.pow(10, nums_after_dot);
        if (n < minVisibleValue) {
          return (
            "< " + minVisibleValue.toFixed(nums_after_dot)
          );
        }
      }
      if (n % 10 === 0) {
        return n;
      }
      return nums_after_dot > -1 ? n.toFixed(nums_after_dot) : n;
    };
    switch (true) {
      case num < 1_000:
        if (always_have) {
          return fixed(num) + "";
        } else {
          return num + "";
        }
      case num >= 1_000 && num < 1_000_000:
        return fixed(num / 1_000) + "K";
      case num >= 1_000_000:
      default:
        return fixed(num / 1_000_000) + "M";
    }
  },
  event_type: (raw_event_type: string): string => {
    // @ts-ignore
    return EventTypeMap[raw_event_type];
  },
  l0NameByCode: (code: number) => {
    const val = INFO_TYPES_BY_CODE_INDEXES[code];
    return val ? val : "?";
  },
  l0Name: (id: string) => {
    const type = l0InfoTypes.value.find((t: any) => t.id === id);
    return type ? type.labelName.split(" ")[0].replace(",", "") : "...";
  },
  infoTypeName: (id: string) => {
    if (id === "ALL_L0_TYPES") return "All";
    const type = allInfoTypes.value[id];
    return type ? type.labelName : "?";
  },
  selectedLabelName: (labels: InformationType[]): string => {
    return InfoTypeHelper.getLabelName(labels);
  },
  selectedContextName: (labels: InformationType[]): string => {
    return InfoTypeHelper.getContextName(labels);
  },
  fileExtension: (name: string): string | undefined => {
    return (name || "").length === 0 || name.indexOf(".") === -1
      ? undefined
      : name.split(".").pop();
  },
  severityLevel(severity: number): string {
    switch (severity) {
      case 0:
        return "none";
      case 1:
      case 0.2:
        return "low";
      case 2:
      case 0.5:
        return "medium";
      case 3:
      case 0.85:
      default:
        return "high";
    }
  },
  compact_num(num: number) {
    return compact_number_formatter.format(num);
  },
  severityLevelColor(severity: number) {
    switch (severity) {
      case 1:
        return "--level-l";
      case 2:
        return "--level-m";
      case 3:
      default:
        return "--level-h";
    }
  },
  entityIcon(p: POPULATION_TYPE) {
    return ICONS.ENTITIES.get(p);
  },
  objectTypeIcon(p: FindingObjectType) {
    return ICONS.FINDINGS_OBJECT_TYPE(p);
  },
  entityStr(p: POPULATION_TYPE, plural = true) {
    return PopulationTypeHelper.asString(p, plural);
  },
  firstWord(words = "", stripChars: string[] = []) {
    const [firstW] = words.split(" ");
    return stripChars.reduce((acc, char) => acc.replace(char, ""), firstW);
  },
  adminIcon(role: UserRole): string {
    return ICONS.ROLE.get(role);
  },
  trend(t: string | number): string {
    const sign = (n: number): string => (n > 0 ? "+" : "-");
    if (typeof t === "number") {
      return sign(t) + " " + t;
    } else {
      const n = Number.parseFloat(t);
      return sign(n) + " " + t;
    }
  },
  remove(str: string, regex: string): string {
    return str.replace(regex, "");
  },
  removeTags(str: string, tags: string[]): string {
    return tags.reduce((lastStr, tag) => {
      const regex = new RegExp(`<${tag}>`, "g");
      return lastStr.replace(regex, "");
    }, str);
  },
  parse(str: string, attr: string): string {
    try {
      return JSON.parse(str)[attr];
    } catch (e) {
      return str;
    }
  },
  fromAxisType(type: AxisType, plurals = false): string {
    let val = "";
    switch (type) {
      case AxisType.None:
        val = "Event";
        break;
      case AxisType.Folder:
      case AxisType.FolderType:
        val = "Folder";
        break;
      case AxisType.Employee:
      case AxisType.EmployeeReceiver:
      case AxisType.EmployeeSender:
      case AxisType.EmployeeReceiverType:
      case AxisType.EmployeeSenderType:
      case AxisType.EmployeeType:
        val = "Employee";
        break;
      case AxisType.OrganizationReceiver:
      case AxisType.OrganizationSender:
      case AxisType.OrganizationReceiverType:
        val = "Organization";
        break;
      case AxisType.PersonalAddress:
      case AxisType.PersonalAddressType:
        val = "Personal contact";
        break;
      case AxisType.ExternalReceiver:
      case AxisType.ExternalSender:
      case AxisType.ExternalReceiverType:
      default:
        val = "External contact";
        break;
    }
    return val + (plurals ? "s" : "");
  },
  toFixed(num: number, count = 2): string {
    if (Number.isNaN(num)) {
      return num + "";
    }
    return num.toFixed(count);
  },
};

Object.entries(Filters).forEach(([fn_name, fn]) => {
  Vue.filter(fn_name, fn);
});

Vue.filter("replaceFirst", (str: string, from: string, to: string) =>
  str.replace(from, to),
);
Vue.filter("lowercase", (str: string) => str.toLowerCase());
Vue.filter("filter", (arr: any[], attr: string, val: any): string[] => {
  return arr.filter((item) => item[attr] === val);
});
Vue.filter("map", (arr: any[], attr: string): string[] => {
  return arr.map((item) => item[attr] as string);
});
Vue.filter("join", (arr: string[], delimiter: string): string => {
  return arr.join(delimiter);
});
